import { useMemo, useState } from "react";
import { categories, feedItems } from "@/mocks/chigua";
import SectionHeading from "./SectionHeading";

interface FeedSectionProps {
  category: string;
  keyword: string;
  onCategory: (c: string) => void;
  onKeyword: (k: string) => void;
}

export default function FeedSection({ category, keyword, onCategory, onKeyword }: FeedSectionProps) {
  const [showAll, setShowAll] = useState(false);
  const [kwInput, setKwInput] = useState(keyword);

  const filtered = useMemo(() => {
    return feedItems.filter((f) => {
      const matchCat = category === "全部" || f.category === category;
      const kw = keyword.trim();
      const matchKw =
        !kw ||
        f.title.includes(kw) ||
        f.summary.includes(kw) ||
        f.tag.includes(kw) ||
        f.category.includes(kw);
      return matchCat && matchKw;
    });
  }, [category, keyword]);

  const visible = showAll ? filtered : filtered.slice(0, 6);

  const submitKw = (e: React.FormEvent) => {
    e.preventDefault();
    onKeyword(kwInput);
  };

  return (
    <section
      id="feed"
      className="scroll-mt-20 bg-background-100 py-14 md:py-20"
    >
      <div className="chigua-container mx-auto">
        <SectionHeading
          eyebrow="事件速递"
          title="吃瓜情报流 · 持续更新"
          desc="按栏目分区的热点速递卡片，点击上方分类或输入关键词，即可精准筛选你想了解的事件脉络。"
        />

        {/* Filter bar */}
        <div className="mt-7 flex flex-col gap-3 rounded-3xl border border-background-200 bg-background-50 p-3 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap items-center gap-1.5" role="tablist" aria-label="事件分类">
            {categories.map((c) => (
              <button
                key={c.key}
                type="button"
                role="tab"
                aria-selected={category === c.key}
                onClick={() => onCategory(c.key)}
                className={`chip ${category === c.key ? "chip-active" : "chip-idle"}`}
              >
                {c.key === "全部" ? <i className="ri-apps-2-line mr-1"></i> : null}
                {c.label}
              </button>
            ))}
          </div>
          <form onSubmit={submitKw} className="flex items-center gap-2 rounded-full border border-background-200 bg-background-50 px-3 py-1.5 md:w-64">
            <i className="ri-search-line text-foreground-400"></i>
            <input
              type="text"
              value={kwInput}
              onChange={(e) => setKwInput(e.target.value)}
              placeholder={category === "全部" ? "搜速递关键词…" : `在${category}中搜索…`}
              className="w-full bg-transparent text-sm outline-none placeholder:text-foreground-400"
            />
            {keyword ? (
              <button
                type="button"
                onClick={() => {
                  onKeyword("");
                  setKwInput("");
                }}
                aria-label="清除关键词"
                className="text-foreground-400 hover:text-primary-600"
              >
                <i className="ri-close-circle-fill"></i>
              </button>
            ) : null}
          </form>
        </div>

        {/* Cards */}
        {visible.length > 0 ? (
          <div className="mt-7 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {visible.map((f) => (
              <article
                key={f.id}
                className="group overflow-hidden rounded-[24px] border border-background-200 bg-background-50 transition-transform duration-300 hover:-translate-y-1"
              >
                <div className="relative aspect-[4/3] w-full overflow-hidden">
                  <img
                    src={f.image}
                    alt={f.title}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className={`absolute left-3 top-3 rounded-full px-2.5 py-1 text-[11px] font-semibold text-background-50 ${f.color}`}>
                    {f.category}
                  </span>
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 text-xs text-foreground-400">
                    <span className="inline-flex items-center gap-1"><i className="ri-time-line"></i>{f.time}</span>
                    <span className="h-1 w-1 rounded-full bg-foreground-300"></span>
                    <span className="inline-flex items-center gap-1"><i className="ri-fire-line text-primary-500"></i>{f.heat}</span>
                  </div>
                  <h3 className="mt-2 line-clamp-2 text-[15px] font-bold leading-snug text-foreground-900 transition-colors group-hover:text-primary-700">
                    {f.title}
                  </h3>
                  <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-500">{f.summary}</p>
                  <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3">
                    <span className="chip chip-idle !px-2.5 !text-[11px]"><i className="ri-price-tag-3-line mr-1"></i>{f.tag}</span>
                    <a href="#deep" className="inline-flex items-center gap-1 text-xs font-semibold text-primary-600 hover:text-primary-700">
                      看全貌 <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="mt-7 flex flex-col items-center justify-center rounded-3xl border border-dashed border-background-300 bg-background-50 py-20 text-center">
            <i className="ri-emotion-sad-line text-4xl text-foreground-300"></i>
            <p className="mt-3 text-sm font-semibold text-foreground-700">该分类下暂没有匹配的情报</p>
            <p className="mt-1 text-xs text-foreground-500">换个关键词，或看看全部情报吧</p>
            <button
              type="button"
              onClick={() => {
                onCategory("全部");
                onKeyword("");
                setKwInput("");
              }}
              className="mt-4 rounded-full bg-primary-500 px-5 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600"
            >
              重置筛选
            </button>
          </div>
        )}

        {/* load more */}
        {filtered.length > 6 && (
          <div className="mt-8 text-center">
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              className="inline-flex items-center gap-2 rounded-full border border-primary-200 bg-background-50 px-6 py-2.5 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-50"
            >
              {showAll ? (
                <>收起 <i className="ri-arrow-up-s-line"></i></>
              ) : (
                <>展开更多情报（共 {filtered.length} 条）<i className="ri-arrow-down-s-line"></i></>
              )}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}